import initialState from '@/lib/defaultState.js'
import events from '@/lib/events.js'
import messages from '@/lib/messages.js'
import store from '@/store.js'

const simulationData = JSON.parse(JSON.stringify(initialState))

let monitorInterval = 0

const storage = window.sessionStorage || window.localStorage

const params = {
  MAJOR_REPAIR: 500,
  MAJOR_REPAIR_DAMAGE_LIMIT: 500,
  MINOR_REPAIR: 100,
  SMOKE_DAMAGE_LIMIT: 300,
  MINOR_REPAIR_DAMAGE_LIMIT: 20,
  ANALYTICS_VALUE: 125,
  END_TIME: 86400,
  PEAK_RATE: 0.15,
  NORMAL_RATE: 0.07,
  REPAIR_GUARANTEED_HOURS: 3,
  MAX_PUMP_INCIDENTS: 3,
  MAX_WORKING_REPAIR_LEVEL: 350,
  FULL_REPAIR_GPM: 320,
  GPM_LOW_DAMAGE: 250,
  GPM_SMALL_DAMAGE: 180,
  GPM_MEDIUM_DAMAGE: 100,
  GPM_HIGH_DAMAGE: 50
}

const intervals = [
  200,
  180,
  150,
  150
]

const initialStates = [
  {
    currentLevel: 55,
    gpmDamageProbability: 0,
    randomPumpDamageProbability: 0.1,
    randomPumpDamageGPM: 50,
    randomPumpFailProbability: 0,
    randomPumpFailGPM: 0,
    towerOverflowTimeout: 240,
    minEventTimeSeconds: 180,
    towerEmptyTimeout: 240
  },
  {
    currentLevel: 55,
    gpmDamageProbability: 0,
    minutesByInterval: 3,
    randomPumpDamageProbability: 0.4,
    randomPumpDamageGPM: 220,
    randomPumpFailProbability: 0.03,
    randomPumpFailGPM: 310,
    towerOverflowTimeout: 180,
    towerEmptyTimeout: 180,
    randomFailIntervalHours: 8,
    minEventTimeSeconds: 30,
    maxRandomPumpFails: 1
  },
  {
    currentLevel: 40,
    currentDemand: 450,
    gpmDamageProbability: 0,
    randomPumpDamageProbability: 0.5,
    randomPumpDamageGPM: 250,
    randomPumpFailProbability: 0.03,
    randomPumpFailGPM: 320,
    randomFailIntervalHours: 10,
    minEventTimeSeconds: 20,
    maxRandomPumpFails: 2,
    pumps: [
      {
        active: true,
        state: 'Running'
      },
      {
        active: true,
        state: 'Running'
      },
      {
        active: false,
        state: 'Stopped'
      }
    ]
  },
  {
    currentLevel: 20,
    currentDemand: 300,
    gpmDamageProbability: 0,
    randomPumpDamageProbability: 0.6,
    randomPumpDamageGPM: 250,
    randomPumpFailProbability: 0.03,
    randomPumpFailGPM: 320,
    randomFailIntervalHours: 7,
    maxRandomPumpFails: 2,
    pumpDamageRepairLevel: 440,
    minEventTimeSeconds: 17,
    pumps: [
      {
        active: true,
        state: 'Running'
      },
      {
        active: false,
        state: 'Stopped',
        gpm: params.GPM_HIGH_DAMAGE,
        repair: params.MAX_WORKING_REPAIR_LEVEL + 1
      },
      {
        active: false,
        state: 'Stopped'
      }
    ]
  }
]

const simulation = {
  data: simulationData,
  params: params,
  challengeNumber: 0,
  running: false,
  started: false,
  defaultActiveAnalytics: [],
  monitorCallback: null,
  initialStateFor: function () {
    const data = JSON.parse(JSON.stringify(initialState))
    data.events = JSON.parse(JSON.stringify(events[simulation.challengeNumber]))
    const challengeState = JSON.parse(JSON.stringify(initialStates[simulation.challengeNumber]))
    for (const key in challengeState) {
      if (typeof data[key] === 'object') {
        for (const key1 in challengeState[key]) {
          if (typeof data[key] === 'object') {
            for (const key2 in challengeState[key][key1]) {
              data[key][key1][key2] = challengeState[key][key1][key2]
            }
          } else {
            data[key][key1] = challengeState[key][key1]
          }
        }
      } else {
        data[key] = challengeState[key]
      }
    }
    data.simulationInterval = intervals[simulation.challengeNumber]
    return data
  },
  stopMonitor: function () {
    simulation.running = false
    clearInterval(monitorInterval)
  },
  startMonitor: function (callback) {
    const interval = simulation.simulationInterval
    if (simulation.running) {
      simulation.stopMonitor()
    }
    simulation.running = true
    simulation.started = true
    if (callback) {
      simulation.monitorCallback = callback
    }
    monitorInterval = setInterval(function () {
      simulation.monitorCallback()
    }, interval)
  },
  forwardToTime: function (time, callback) {
    simulation.stopMonitor()
    simulation.running = true
    clearInterval(simulation.fastForwardProc)
    simulation.fastForwardProc = setInterval(function () {
      if (simulationData.currentTime < time) {
        simulation.monitorCallback()
      } else {
        clearInterval(simulation.fastForwardProc)
        if (callback) {
          callback()
        }
      }
    }, 20)
  },
  askPumpRepair: function (pump) {
    pump.askRepair = true
    pump.repairValue = (pump.repair >= params.MAJOR_REPAIR_DAMAGE_LIMIT) ? params.MAJOR_REPAIR : params.MINOR_REPAIR
  },
  askAnalytics: function (pump) {
    if (pump.analytics) {
      return
    }
    pump.askAnalytics = true
  },
  hideBaloons: function () {
    const elements = document.querySelectorAll('.chat-baloon')
    for (let idx = 0; idx < elements.length; idx++) {
      elements[idx].style.display = ''
    }
  },
  showBaloons: function () {
    const elements = document.querySelectorAll('.chat-baloon')
    document.querySelector('#baloon-1').setAttribute('type', 'normal')
    for (let idx = 0; idx < elements.length; idx++) {
      elements[idx].style.display = 'block'
    }
  },
  viewMessages: function () {
    document.location = '#/challenge/0/messages'
  },
  doRepair: function (pump) {
    simulationData.totalCost += pump.repairValue
    simulationData.repairCost += pump.repairValue
    if (pump.analytics === false && pump.repairValue > params.MINOR_REPAIR) {
      simulationData.analyticsMissed = pump.repairValue
    }
    if (pump.repairValue <= params.MINOR_REPAIR) {
      simulationData.pumpIssues++
    }
    pump.lastRepair = simulationData.currentTime
    pump.preventFail = false
    pump.active = false
    if (pump.repairValue <= params.MINOR_REPAIR) {
      pump.repair = params.MINOR_REPAIR
    } else {
      pump.repair = params.MAJOR_REPAIR
    }
    pump.repairTotal = pump.repair
    pump.askRepair = false
    pump.state = 'Repair'
    pump.percent = 1
  },
  doAnalytics: function (pump) {
    if (pump.analytics) {
      return
    }
    simulationData.totalCost += simulation.params.ANALYTICS_VALUE
    simulationData.analyticsCost += simulation.params.ANALYTICS_VALUE
    simulationData.analyticsPumps++
    pump.askAnalytics = false
    pump.analytics = true
  },
  registerFirstFail: function () {
    store.state.popups.firstFail = true
    store.dispatch('store')
  },
  action: function (action) {
    if (action === 'activate-pump-2') {
      const pump = simulationData.pumps[1]
      pump.active = true
      pump.state = 'Running'
    }
    if (action === 'activate-pump-3') {
      const pump = simulationData.pumps[2]
      pump.active = true
      pump.state = 'Running'
    }
    if (action === 'deactivate-pump-1') {
      const pump = simulationData.pumps[0]
      pump.active = false
      pump.state = 'Stopped'
    }
    if (action === 'deactivate-pump-2') {
      const pump = simulationData.pumps[1]
      pump.active = false
      pump.state = 'Stopped'
    }
    if (action === 'deactivate-pump-3') {
      const pump = simulationData.pumps[2]
      pump.active = false
      pump.state = 'Stopped'
    }
    if (action === 'askrepair-pump-1') {
      simulation.askPumpRepair(simulationData.pumps[0])
    }
    if (action === 'repair-pump-1') {
      simulation.doRepair(simulationData.pumps[0])
    }
    if (this[action]) {
      this[action]()
    }
  },
  clear: function () {
    simulation.stopMonitor()
    simulation.reset()
    simulation.save()
    simulation.defaultActiveAnalytics = []
  },
  reset: function () {
    for (const key in initialState) {
      const simData = simulation.initialStateFor()[key]
      simulationData[key] = JSON.parse(JSON.stringify(simData))
    }
    messages.currentChallenge = simulation.challengeNumber
    messages.reset()
  },
  restore: function () {
    let saved = storage.getItem('game.state')
    if (saved && saved !== '') {
      saved = JSON.parse(saved)
      for (const key in saved) {
        this[key] = simulationData[key]
      }
    }
  },
  save: function () {
    const saved = {}
    for (const key in initialState) {
      saved[key] = simulationData[key]
    }
    storage.setItem('game.state', JSON.stringify(saved))
  }
}

export default simulation
